// PURPOSE: Best-effort browser crash capture. CONTRACT: Only safe code metadata leaves the browser. DEPENDS: fetch; spec 060. CEILING: No tokens, message text, raw stack, URLs or identities.
type Source = 'error' | 'rejection' | 'boundary';
type Frame = { file: string; line: number; function: string };
const names = new Set(['Error', 'TypeError', 'RangeError', 'ReferenceError', 'SyntaxError', 'URIError', 'EvalError', 'AggregateError']);
const seen = new Map<string, number>();
const pending: { exception_type: string; source: Source; frames: Frame[] }[] = [];
let running = false;
let warned = false;
let captured = new WeakMap<Error, number>();
let drops = 0;
let lastDropWarning = -Infinity;

function codeFrames(value: unknown): Frame[] {
  if (!(value instanceof Error) || typeof value.stack !== 'string') return [];
  const frames: Frame[] = [];
  for (const line of value.stack.slice(0, 16000).split('\n').slice(1, 31)) {
    const match = line.match(/(?:at\s+([A-Za-z_$][A-Za-z0-9_$.]{0,79})\s+\()?((?:https?:\/\/[^/\s]+)?\/(?:src\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+\.tsx?|assets\/[A-Za-z0-9_-]+\.js))(?:\?[^\s]*)?:(\d+):\d+\)?$/);
    if (!match) continue;
    const path = new URL(match[2], window.location.origin).pathname;
    const file = path.startsWith('/src/') ? 'frontend' + path : 'frontend/dist' + path;
    const number = Number(match[3]);
    if (file.length <= 200 && number > 0 && number <= 10000000) frames.push({ file, line: number, function: match[1] || 'anonymous' });
  }
  return frames;
}

function warning() {
  try { if (!warned) { warned = true; console.warn('Browser failure reporting unavailable.'); } } catch { /* Reporting cannot crash the app. */ }
}

async function drain() {
  if (running) return;
  running = true;
  try {
    while (pending.length) {
      const payload = pending.shift()!;
      for (let attempt = 0; attempt < 2; attempt++) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        try {
          const response = await fetch('/api/client-errors', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            credentials: 'omit', referrerPolicy: 'no-referrer',
            body: JSON.stringify(payload), signal: controller.signal,
          });
          if (!response.ok) throw new Error('Report rejected');
          break;
        } catch { if (attempt === 1) warning(); }
        finally { clearTimeout(timeout); }
      }
    }
  } finally { running = false; }
}

export function captureBrowserError(value: unknown, source: Source) {
  try {
    const name = value instanceof Error && names.has(value.name) ? value.name : 'Error';
    const now = Date.now();
    if (value instanceof Error && captured.has(value) && now - captured.get(value)! < 60_000) return;
    const frames = codeFrames(value);
    const key = JSON.stringify([source, name, frames.map(frame => [frame.file, frame.function])]);
    for (const [old, time] of seen) if (now - time >= 60_000) seen.delete(old);
    if (seen.has(key) && now - seen.get(key)! < 60_000) return;
    if (pending.length >= 100 || seen.size >= 1000) {
      drops++;
      if (now - lastDropWarning >= 60_000) { lastDropWarning = now; console.warn('Browser report queue full; reports dropped:', drops); }
      return;
    }
    seen.set(key, now);
    if (value instanceof Error) captured.set(value, now);
    pending.push({ exception_type: name, source, frames });
    void drain().catch(warning);
  } catch { warning(); }
}

export function installErrorCapture() {
  const error = (event: ErrorEvent) => captureBrowserError(event.error, 'error');
  const rejection = (event: PromiseRejectionEvent) => captureBrowserError(event.reason, 'rejection');
  window.addEventListener('error', error);
  window.addEventListener('unhandledrejection', rejection);
  return () => {
    window.removeEventListener('error', error);
    window.removeEventListener('unhandledrejection', rejection);
  };
}

export function resetErrorCapture() { seen.clear(); pending.length = 0; warned = false; captured = new WeakMap(); drops = 0; lastDropWarning = -Infinity; }
